import type { PropEffect } from '@/types/props';
import type { GameState, Position } from '@/types/game';

export class PropEffectsManager {
  private activeEffects: Map<string, { effect: PropEffect; startTime: number }> = new Map();
  
  constructor() {
    // Initialize effects manager
  }
  
  // Apply all equipped prop effects
  applyEffects(effects: PropEffect[], gameState: GameState): Partial<GameState> {
    const stateModifications: Partial<GameState> = {};
    
    effects.forEach(effect => {
      switch (effect.type) {
        case 'speed':
          // Speed effects are handled in movement logic
          break;
          
        case 'shield':
          // Shield effects are passive and handled in collision logic
          break;
          
        case 'magnet':
          // Apply magnet effect to collectibles
          stateModifications.collectibles = this.applyMagnetEffect(
            gameState.collectibles,
            gameState.position,
            effect.value
          );
          break;
          
        case 'vision':
          // Vision effects reveal hidden tiles (handled in rendering)
          break;
          
        case 'luck':
          // Luck effects increase coin values (handled in collection)
          break;
          
        case 'score':
          // Score multipliers are applied in scoring logic
          break;
      }
    });
    
    return stateModifications;
  }
  
  // Activate a temporary effect
  activateTemporaryEffect(effect: PropEffect) {
    if (effect.duration) {
      const key = `${effect.type}-${Date.now()}`;
      this.activeEffects.set(key, {
        effect,
        startTime: Date.now()
      });
      
      // Auto-remove after duration
      setTimeout(() => {
        this.activeEffects.delete(key);
      }, effect.duration * 1000);
    }
  }
  
  // Get all currently active effects
  getActiveEffects(): PropEffect[] {
    const now = Date.now();
    const active: PropEffect[] = [];
    
    // Clean up expired effects
    this.activeEffects.forEach((value, key) => {
      if (value.effect.duration) {
        const elapsed = now - value.startTime;
        if (elapsed >= value.effect.duration * 1000) {
          this.activeEffects.delete(key);
        } else {
          active.push(value.effect);
        }
      }
    });
    
    return active;
  }
  
  // Apply magnet effect to collectibles
  private applyMagnetEffect(
    collectibles: GameState['collectibles'],
    playerPos: Position,
    range: number
  ): GameState['collectibles'] {
    return collectibles.map(collectible => {
      if (collectible.isCollected) return collectible;
      
      const distance = Math.abs(collectible.position[0] - playerPos[0]) + 
                      Math.abs(collectible.position[1] - playerPos[1]);
      
      // If within magnet range, mark as collected
      if (distance <= range) {
        return { ...collectible, isCollected: true };
      }
      
      return collectible;
    });
  }
  
  // Check if player has shield protection
  hasShieldProtection(effects: PropEffect[]): boolean {
    return effects.some(effect => effect.type === 'shield' && effect.value > 0);
  }
  
  // Check if player can phase through walls
  canPhase(effects: PropEffect[]): boolean {
    const activeEffects = this.getActiveEffects();
    return effects.some(effect => effect.type === 'phase') || 
           activeEffects.some(effect => effect.type === 'phase');
  }
  
  // Get movement speed multiplier
  getSpeedMultiplier(effects: PropEffect[]): number {
    const speedEffect = effects.find(effect => effect.type === 'speed');
    const activeSpeedEffect = this.getActiveEffects().find(effect => effect.type === 'speed');
    
    // Use the highest speed multiplier
    const passiveSpeed = speedEffect?.value || 1;
    const activeSpeed = activeSpeedEffect?.value || 1;
    
    return Math.max(passiveSpeed, activeSpeed);
  }
  
  // Get score multiplier
  getScoreMultiplier(effects: PropEffect[]): number {
    const scoreEffects = effects.filter(effect => effect.type === 'score');
    const multiplier = scoreEffects.reduce((acc, effect) => acc * effect.value, 1);
    return multiplier;
  }
  
  // Get coin value multiplier (luck)
  getCoinMultiplier(effects: PropEffect[]): number {
    const luckEffects = effects.filter(effect => effect.type === 'luck');
    const multiplier = luckEffects.reduce((acc, effect) => acc * effect.value, 1);
    return multiplier;
  }
  
  // Get vision range
  getVisionRange(effects: PropEffect[]): number {
    const visionEffect = effects.find(effect => effect.type === 'vision');
    return visionEffect?.value || 0;
  }
  
  // Apply freeze effect to nearby obstacles
  applyFreezeEffect(
    position: Position,
    range: number,
    _duration: number,
    rocks: GameState['rocks']
  ): string[] {
    const frozenRocks: string[] = [];
    
    rocks.forEach(rock => {
      const distance = Math.abs(rock[0] - position[0]) + Math.abs(rock[1] - position[1]);
      if (distance <= range && (rock[2] === 'stop' || rock[2] === 'lava')) {
        frozenRocks.push(`${rock[0]},${rock[1]}`);
      }
    });
    
    return frozenRocks;
  }
  
  // Check if an obstacle is frozen
  isObstacleFrozen(position: Position, frozenObstacles: string[]): boolean {
    return frozenObstacles.includes(`${position[0]},${position[1]}`);
  }
  
  // Handle teleport effect
  teleport(
    currentPos: Position,
    targetPos: Position,
    maxRange: number,
    gameState: GameState
  ): Position | null {
    const distance = Math.abs(targetPos[0] - currentPos[0]) + 
                    Math.abs(targetPos[1] - currentPos[1]);
    
    if (distance > maxRange) return null;
    
    // Check if target position is valid (not a wall)
    const isWall = gameState.rocks.some(rock => 
      rock[0] === targetPos[0] && rock[1] === targetPos[1] && rock[2] === 'rock'
    );
    
    if (isWall) return null;
    
    return targetPos;
  }
  
  // Handle bounce effect (jump over tiles)
  bounce(
    position: Position,
    direction: 'up' | 'down' | 'left' | 'right',
    jumpDistance: number,
    gameState: GameState
  ): Position {
    let newPos: Position = [...position];
    
    switch (direction) {
      case 'up':
        newPos[1] = Math.max(0, position[1] - jumpDistance);
        break;
      case 'down':
        newPos[1] = Math.min(gameState.rocks.length - 1, position[1] + jumpDistance);
        break;
      case 'left':
        newPos[0] = Math.max(0, position[0] - jumpDistance);
        break;
      case 'right':
        newPos[0] = Math.min(gameState.width - 1, position[0] + jumpDistance);
        break;
    }
    
    return newPos;
  }
}

// Singleton instance
let propEffectsManager: PropEffectsManager | null = null;

export function getPropEffectsManager(): PropEffectsManager {
  if (!propEffectsManager) {
    propEffectsManager = new PropEffectsManager();
  }
  return propEffectsManager;
}